import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getCategoryLabel } from "../utils/categoryUtils";
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import Layout from "../components/layout/Layout";
import { useControlCash } from "../store/controlCashStore";
import { useToast } from "../contexts/ToastContext";
import { useConfirm } from "../contexts/ConfirmContext";

ChartJS.register(ArcElement, Tooltip, Legend);

// ============================================================================
// HELPERS
// ============================================================================
const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString("es-PE", { 
    day: "2-digit", 
    month: "short" 
  });
};

// ============================================================================
// COMPONENTE: PageHeader
// ============================================================================
const PageHeader = ({ monthName, onBack, onEditIncomes, onDeleteMonth }) => {
  return (
    <div className="mb-8">
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium mb-4 transition-colors group"
        aria-label="Volver al inicio"
      >
        <svg 
          className="w-5 h-5 transform group-hover:-translate-x-1 transition-transform" 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
        Volver
      </button>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 dark:text-gray-100 mb-2">
            {monthName}
          </h1>
          <p className="text-gray-600 dark:text-gray-400">Detalle del mes</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={onEditIncomes}
            className="px-4 py-2 bg-green-100 hover:bg-green-200 text-green-700 font-semibold rounded-xl transition-colors flex items-center gap-2"
            aria-label="Editar ingresos"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Editar Ingresos
          </button>
          {onDeleteMonth && (
            <button
              onClick={onDeleteMonth}
              className="px-4 py-2 bg-red-100 hover:bg-red-200 text-red-700 font-semibold rounded-xl transition-colors flex items-center gap-2"
              aria-label="Eliminar mes"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
               
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// COMPONENTE: ExpensesChart
// ============================================================================
const ExpensesChart = ({ expensesByCategory, displayCategories, getCategoryLabel, formatAmount }) => {
  const chartColors = ["#8b5cf6", "#6366f1", "#ec4899", "#14b8a6", "#f59e0b", "#ef4444"];

  const dataValues = displayCategories.map((key) =>
    (expensesByCategory[key] || []).reduce((sum, e) => sum + e.amount, 0)
  );
  const total = dataValues.reduce((a, b) => a + b, 0);

  if (total === 0 || displayCategories.length === 0) return null;

  const chartData = {
    labels: displayCategories.map((key, i) => `${getCategoryLabel(key)}: ${formatAmount(dataValues[i])}`),
    datasets: [
      {
        data: dataValues,
        backgroundColor: displayCategories.map((_, i) => chartColors[i % chartColors.length]),
        hoverBackgroundColor: displayCategories.map((_, i) => `${chartColors[i % chartColors.length]}CC`),
        borderWidth: 2,
        borderColor: "#fff",
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        position: "bottom",
      },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const pct = total > 0 ? ((ctx.raw / total) * 100).toFixed(1) : 0;
            return `${ctx.label} (${pct}%)`;
          },
        },
      },
    },
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 mb-6">
      <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-4">
        Distribución de gastos por categoría
      </h3>
      <div className="max-w-xs mx-auto">
        <Doughnut data={chartData} options={options} />
      </div>
    </div>
  );
};

// ============================================================================
// COMPONENTE: SummaryCards
// ============================================================================
const SummaryCards = ({ summary, formatAmount }) => {
  const cards = [
    {
      label: "Ingresos del Mes",
      amount: summary.income,
      gradient: "from-green-500 to-green-600",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    },
    {
      label: "Total Comprometido",
      amount: summary.totalCommitted,
      gradient: "from-orange-500 to-red-500",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
        </svg>
      ),
      subtitle: `Pagado: ${formatAmount(summary.totalPaid)} | Pendiente: ${formatAmount(summary.totalPending)}`
    },
    {
      label: "Pagado Hasta Hoy",
      amount: summary.totalPaid,
      gradient: "from-blue-500 to-indigo-600",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
      subtitle: `Gastos reales descontados`
    },
    {
      label: "Restante Disponible",
      amount: summary.availableRemaining,
      gradient: summary.availableRemaining >= 0 ? "from-emerald-500 to-teal-600" : "from-red-600 to-red-700",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      ),
      subtitle: `Dinero real disponible`
    }
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
      {cards.map((card, index) => (
        <div
          key={index}
          className={`bg-gradient-to-br ${card.gradient} rounded-xl p-4 text-white shadow-md`}
        >
          <div className="flex items-center gap-2 mb-2">
            <div className="opacity-80 scale-90">{card.icon}</div>
            <p className="text-xs font-medium opacity-90">{card.label}</p>
          </div>
          <p className="text-2xl font-bold mb-1">
            {formatAmount(card.amount)}
          </p>
          {card.subtitle && (
            <p className="text-xs opacity-75">{card.subtitle}</p>
          )}
        </div>
      ))}
    </div>
  );
};

// ============================================================================
// COMPONENTE: ExpensesListCard
// ============================================================================
const ExpensesListCard = ({ title, expenses, formatAmount, color, icon, onEditExpense, onDeleteExpense, onToggleStatus, emptyMessage }) => {
  const [isCollapsed, setIsCollapsed] = React.useState(false);
  const total = expenses.reduce((sum, exp) => sum + exp.amount, 0);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden">
      <div 
        className={`bg-gradient-to-r ${color} p-4 cursor-pointer hover:opacity-95 transition-opacity`}
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-white/30 flex items-center justify-center">
              {React.cloneElement(icon, { className: "w-5 h-5 text-white" })}
            </div>
            <div>
              <h3 className="text-base font-bold text-white">{title}</h3>
              <p className="text-xs text-white/90">{expenses.length} {expenses.length === 1 ? 'gasto' : 'gastos'}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-lg font-bold text-white">
                {formatAmount(total)}
              </p>
            </div>
            <svg 
              className={`w-5 h-5 text-white transition-transform ${isCollapsed ? '' : 'rotate-180'}`}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </div>

      {!isCollapsed && (
        <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto">

        {expenses.length === 0 ? (
          <div className="p-8 text-center text-gray-400">
            <svg className="w-12 h-12 mx-auto mb-2 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm font-medium">{emptyMessage}</p>
          </div>
        ) : (
          expenses.map((expense) => (
            <div
              key={expense.id}
              className="p-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors group"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-semibold text-sm text-gray-800 truncate">
                      {expense.name}
                    </h4>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-gray-600">
                    <span className="font-bold text-gray-900">
                      {formatAmount(expense.amount)}
                    </span>
                    {expense.dueDay && (
                      <span className="text-orange-600">
                        Día {expense.dueDay}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-1">
                  {/* Botón marcar como pagado/pendiente */}
                  {onToggleStatus && (
                    <button
                      onClick={() => onToggleStatus(expense.id)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors flex items-center gap-1 ${
                        expense.status === "PENDING"
                          ? "bg-blue-600 hover:bg-blue-700 text-white"
                          : "bg-orange-500 hover:bg-orange-600 text-white"
                      }`}
                      aria-label={expense.status === "PENDING" ? "Marcar como pagado" : "Deshacer pago"}
                    >
                      {expense.status === "PENDING" ? (
                        <>
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          Pagar
                        </>
                      ) : (
                        <>
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                          </svg>
                          Deshacer
                        </>
                      )}
                    </button>
                  )}

                  {/* Botones de acción */}
                  <div className="flex items-center gap-0.5">
                    {/* Botón editar */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditExpense && onEditExpense(expense.id);
                      }}
                      className="p-1.5 hover:bg-blue-100 rounded transition-colors text-blue-600"
                      aria-label="Editar gasto"
                      title="Editar"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    
                    {/* Botón eliminar */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteExpense && onDeleteExpense(expense.id, expense.name);
                      }}
                      className="p-1.5 hover:bg-red-100 rounded transition-colors text-red-600"
                      aria-label="Eliminar gasto"
                      title="Eliminar"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// COMPONENTE: ExpenseGroupCard (LEGACY - mantener para compatibilidad)
// ============================================================================
const ExpenseGroupCard = ({ title, expenses, formatAmount, icon, color, onEditExpense, onDeleteExpense }) => {
  const [isCollapsed, setIsCollapsed] = React.useState(true);
  const total = expenses.reduce((sum, exp) => sum + exp.amount, 0);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden">
      <div 
        className={`bg-gradient-to-r ${color} p-3 cursor-pointer hover:opacity-95 transition-opacity`}
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
              {React.cloneElement(icon, { className: "w-4 h-4 text-white" })}
            </div>
            <div>
              <h3 className="text-sm font-bold text-white">{title}</h3>
              <p className="text-xs text-white/80">{expenses.length} gastos</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-right">
              <p className="text-sm font-bold text-white">
                {formatAmount(total)}
              </p>
            </div>
            <svg 
              className={`w-4 h-4 text-white transition-transform ${isCollapsed ? '' : 'rotate-180'}`}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </div>

      {!isCollapsed && (
        <div className="divide-y divide-gray-100 max-h-64 overflow-y-auto">
          {expenses.length === 0 ? (
            <div className="p-6 text-center text-gray-400">
              <svg className="w-10 h-10 mx-auto mb-2 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
              <p className="text-xs">No hay gastos registrados</p>
            </div>
          ) : (
            expenses.map((expense) => (
              <div
                key={expense.id}
                className="p-2.5 hover:bg-gray-50 transition-colors group"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-sm text-gray-800 truncate">
                      {expense.name}
                    </h4>
                    {expense.date && (
                      <p className="text-xs text-gray-500">
                        {formatDate(expense.date)}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <p className="font-bold text-sm text-gray-900">
                      {formatAmount(expense.amount)}
                    </p>
                    
                    {/* Botones de acción */}
                    <div className="flex items-center gap-0.5">
                      {/* Botón editar */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditExpense && onEditExpense(expense.id);
                        }}
                        className="p-1.5 hover:bg-blue-100 rounded transition-colors text-blue-600"
                        aria-label="Editar gasto"
                        title="Editar"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      
                      {/* Botón eliminar */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteExpense && onDeleteExpense(expense.id, expense.name);
                        }}
                        className="p-1.5 hover:bg-red-100 rounded transition-colors text-red-600"
                        aria-label="Eliminar gasto"
                        title="Eliminar"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// COMPONENTE PRINCIPAL: MonthDetail
// ============================================================================
// ============================================================================
// COMPONENTE: ExpenseFilters
// ============================================================================
const ExpenseFilters = ({ search, setSearch, filterCategory, setFilterCategory, categoryIds, getCategoryLabel }) => (
  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-4 mb-6 flex flex-wrap gap-3 items-center">
    <div className="flex-1 min-w-[180px]">
      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Buscar por nombre..."
        className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      />
    </div>
    <div className="min-w-[160px]">
      <select
        value={filterCategory}
        onChange={(e) => setFilterCategory(e.target.value || "")}
        className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      >
        <option value="">Todas las categorías</option>
        {categoryIds.map((catId) => (
          <option key={catId} value={catId}>{getCategoryLabel(catId)}</option>
        ))}
      </select>
    </div>
    {(search || filterCategory) && (
      <button
        onClick={() => { setSearch(""); setFilterCategory(""); }}
        className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
      >
        Limpiar filtros
      </button>
    )}
  </div>
);

// ============================================================================
// COMPONENTE PRINCIPAL: MonthDetail
// ============================================================================
const MonthDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { state, dispatch, getMonthById, getMonthTotals, getMonthExpenses, formatDisplayAmount } = useControlCash();
  const toast = useToast();
  const { confirm } = useConfirm();
  const [showRecurringModal, setShowRecurringModal] = React.useState(false);
  const [selectedTemplates, setSelectedTemplates] = React.useState([]);
  const [filterSearch, setFilterSearch] = React.useState("");
  const [filterCategory, setFilterCategory] = React.useState("");

  // Obtener el mes del store
  const month = getMonthById(id);

  // Si no existe el mes, mostrar mensaje
  if (!month) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-md p-12 text-center">
            <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-2">Mes no encontrado</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">El mes que buscas no existe</p>
            <button
              onClick={() => navigate("/")}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl shadow-md transition-colors"
            >
              Volver al inicio
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  // Obtener datos del mes
  const totals = getMonthTotals(id);
  const allExpenses = getMonthExpenses(id);

  // Separar gastos pendientes y pagados
  const pendingExpenses = allExpenses.filter(e => e.status === "PENDING");
  const paidExpenses = allExpenses.filter(e => e.status === "PAID");

  // Normalizar categoría (legacy labels para datos antiguos)
  const normalizeCat = (c) => {
    if (!c) return "_other";
    if (c === "Gastos Fijos") return "fixed";
    if (c === "Suscripciones") return "subscriptions";
    if (c === "Extras") return "extras";
    return c;
  };

  const customCategories = Array.isArray(state.customCategories) ? state.customCategories : [];

  // Agrupar gastos por categoría
  const expensesByCategory = {};
  allExpenses.forEach(e => {
    const key = normalizeCat(e.category);
    if (!expensesByCategory[key]) expensesByCategory[key] = [];
    expensesByCategory[key].push(e);
  });

  const getCategoryLabelLocal = (key) => getCategoryLabel(key, customCategories);

  const CATEGORY_COLORS = ["from-purple-500 to-purple-600", "from-indigo-500 to-indigo-600", "from-pink-500 to-pink-600", "from-teal-500 to-teal-600", "from-amber-500 to-amber-600"];
  const getCategoryColor = (key) => {
    const idx = customCategories.findIndex(c => c.id === key);
    return CATEGORY_COLORS[idx >= 0 ? idx % CATEGORY_COLORS.length : 0];
  };

  const displayCategories = [...customCategories.map(c => c.id), "_other"].filter(k => (expensesByCategory[k]?.length || 0) > 0);

  // Aplicar filtros a los gastos
  const filterExpenses = (list) => {
    return list.filter((e) => {
      const nameMatch = !filterSearch.trim() || e.name?.toLowerCase().includes(filterSearch.trim().toLowerCase());
      const catKey = normalizeCat(e.category);
      const catMatch = !filterCategory || catKey === filterCategory;
      return nameMatch && catMatch;
    });
  };

  const filteredPendingExpenses = filterExpenses(pendingExpenses);
  const filteredPaidExpenses = filterExpenses(paidExpenses);

  // Gastos por categoría filtrados (para las tarjetas de categoría)
  const filteredExpensesByCategory = {};
  const catsToShow = filterCategory ? [filterCategory] : displayCategories;
  catsToShow.forEach((key) => {
    const list = (expensesByCategory[key] || []).filter((e) => {
      if (!filterSearch.trim()) return true;
      return e.name?.toLowerCase().includes(filterSearch.trim().toLowerCase());
    });
    if (list.length > 0) filteredExpensesByCategory[key] = list;
  });

  const filteredDisplayCategories = Object.keys(filteredExpensesByCategory);

  // Calcular plantillas disponibles para el botón "Seleccionar todos"
  const availableTemplatesForSelection = state.recurringTemplates.filter(t => {
    if (!t.active) return false;
    const existingTemplateIds = allExpenses
      .filter(e => e.isFromRecurring && e.recurringTemplateId)
      .map(e => e.recurringTemplateId);
    return !existingTemplateIds.includes(t.id);
  });
  const allTemplatesSelected = selectedTemplates.length === availableTemplatesForSelection.length && availableTemplatesForSelection.length > 0;

  const handleToggleBillStatus = (billId) => {
    dispatch({
      type: "TOGGLE_EXPENSE_PAID",
      payload: billId
    });
  };

  const handleAddExpense = () => {
    navigate(`/expense/add?monthId=${id}`);
  };

  const handleEditExpense = (expenseId) => {
    navigate(`/expense/edit/${expenseId}`);
  };

  const handleDeleteExpense = async (expenseId, expenseName) => {
    const ok = await confirm({
      title: "Eliminar gasto",
      message: `¿Estás seguro de eliminar el gasto "${expenseName}"?\n\nEsta acción no se puede deshacer.`,
      confirmLabel: "Eliminar"
    });
    if (ok) {
      dispatch({ type: "DELETE_EXPENSE", payload: expenseId });
    }
  };

  const handleBack = () => {
    navigate("/");
  };

  const handleEditIncomes = () => {
    navigate(`/month/${id}/edit`);
  };

  const handleDeleteMonth = async () => {
    const ok = await confirm({
      title: "Eliminar mes",
      message: `¿Estás seguro de eliminar "${month.name}"?\n\nSe eliminarán también los ingresos y gastos de este mes.`,
      confirmLabel: "Eliminar"
    });
    if (ok) {
      dispatch({ type: "DELETE_MONTH", payload: id });
      navigate("/");
    }
  };

  const handleOpenRecurringModal = () => {
    // Filtrar plantillas activas que no estén ya agregadas al mes
    const existingTemplateIds = allExpenses
      .filter(e => e.isFromRecurring && e.recurringTemplateId)
      .map(e => e.recurringTemplateId);
    
    const availableTemplates = state.recurringTemplates.filter(
      t => t.active && !existingTemplateIds.includes(t.id)
    );

    if (availableTemplates.length === 0) {
      toast.warning("No hay plantillas recurrentes disponibles para agregar.\n\nTodas las plantillas activas ya están en este mes, o no tienes plantillas activas.");
      return;
    }

    setShowRecurringModal(true);
  };

  const handleToggleTemplate = (templateId) => {
    setSelectedTemplates(prev => 
      prev.includes(templateId)
        ? prev.filter(id => id !== templateId)
        : [...prev, templateId]
    );
  };

  const handleSelectAllTemplates = () => {
    const monthExpenses = state.expenses.filter(e => e.monthId === id);
    const availableTemplates = state.recurringTemplates
      .filter(t => {
        // Filtrar solo activas y que no estén ya en el mes
        if (!t.active) return false;
        const existingTemplateIds = monthExpenses
          .filter(e => e.isFromRecurring && e.recurringTemplateId)
          .map(e => e.recurringTemplateId);
        return !existingTemplateIds.includes(t.id);
      })
      .map(t => t.id);

    // Si ya están todos seleccionados, deseleccionar todos
    if (selectedTemplates.length === availableTemplates.length) {
      setSelectedTemplates([]);
    } else {
      // Seleccionar todos
      setSelectedTemplates(availableTemplates);
    }
  };

  const handleAddRecurringExpenses = () => {
    if (selectedTemplates.length === 0) {
      toast.warning("Selecciona al menos una plantilla para agregar");
      return;
    }

    selectedTemplates.forEach(templateId => {
      const template = state.recurringTemplates.find(t => t.id === templateId);
      if (template) {
        dispatch({
          type: "ADD_EXPENSE",
          payload: {
            monthId: id,
            name: template.name,
            amount: template.amount,
            category: template.category,
            status: "PENDING",
            dueDay: template.dueDay,
            isFromRecurring: true,
            recurringTemplateId: template.id,
            remindDaysBefore: template.remindDaysBefore,
            date: new Date().toISOString(),
            paidAt: null
          }
        });
      }
    });

    setShowRecurringModal(false);
    setSelectedTemplates([]);
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <PageHeader monthName={month.name} onBack={handleBack} onEditIncomes={handleEditIncomes} onDeleteMonth={handleDeleteMonth} />

        {/* Resumen del Mes */}
        <SummaryCards 
          summary={totals} 
          formatAmount={formatDisplayAmount} 
        />

        {/* Botones de acción */}
        <div className="flex items-center justify-end gap-2 mb-6">
          <button
            onClick={handleOpenRecurringModal}
            className="px-4 py-3 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-xl shadow-md transition-colors flex items-center gap-2"
            aria-label="Agregar desde plantillas"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <span className="hidden sm:inline">Desde plantillas</span>
          </button>
          <button
            onClick={handleAddExpense}
            className="px-5 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl shadow-md transition-colors flex items-center gap-2"
            aria-label="Agregar nuevo gasto"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span className="hidden sm:inline">Agregar gasto</span>
          </button>
        </div>

        {/* Gastos Pendientes y Pagados - Dos columnas en desktop */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Gastos Pendientes */}
          <ExpensesListCard
            title="Gastos Pendientes"
            expenses={filteredPendingExpenses}
            formatAmount={formatDisplayAmount}
            color="from-orange-500 to-red-500"
            icon={
              <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
            onEditExpense={handleEditExpense}
            onDeleteExpense={handleDeleteExpense}
            onToggleStatus={handleToggleBillStatus}
            emptyMessage="¡Genial! No tienes gastos pendientes"
          />

          {/* Gastos Pagados */}
          <ExpensesListCard
            title="Gastos Pagados"
            expenses={filteredPaidExpenses}
            formatAmount={formatDisplayAmount}
            color="from-green-500 to-emerald-600"
            icon={
              <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            }
            onEditExpense={handleEditExpense}
            onDeleteExpense={handleDeleteExpense}
            onToggleStatus={handleToggleBillStatus}
            emptyMessage="Aún no has pagado ningún gasto"
          />
        </div>

        {/* Distribución de gastos por categoría */}
        <ExpensesChart
          expensesByCategory={expensesByCategory}
          displayCategories={displayCategories}
          getCategoryLabel={getCategoryLabelLocal}
          formatAmount={formatDisplayAmount}
        />

        {/* Separador */}
        <div className="border-t-2 border-gray-200 dark:border-gray-700 my-8"></div>

        {/* Filtros de gastos */}
        <ExpenseFilters
          search={filterSearch}
          setSearch={setFilterSearch}
          filterCategory={filterCategory}
          setFilterCategory={setFilterCategory}
          categoryIds={[...customCategories.map(c => c.id), "_other"]}
          getCategoryLabel={getCategoryLabelLocal}
        />

        {/* Gastos por Categoría */}
        <div className="mb-6">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Gastos por Categoría</h2>
            <p className="text-gray-600 dark:text-gray-400 text-sm">Vista organizada por tipo de gasto</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {filteredDisplayCategories.map((key) => (
              <ExpenseGroupCard
                key={key}
                title={getCategoryLabelLocal(key)}
                expenses={filteredExpensesByCategory[key] || []}
                formatAmount={formatDisplayAmount}
                color={getCategoryColor(key)}
                onEditExpense={handleEditExpense}
                onDeleteExpense={handleDeleteExpense}
                icon={
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                }
              />
            ))}
          </div>
        </div>

        {/* Modal para agregar gastos recurrentes */}
        {showRecurringModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-purple-600 to-purple-700 p-6 text-white">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="text-2xl font-bold">Agregar Gastos Recurrentes</h3>
                    <p className="text-purple-100 text-sm mt-1">Selecciona las plantillas que quieres agregar a este mes</p>
                  </div>
                  <button
                    onClick={() => {
                      setShowRecurringModal(false);
                      setSelectedTemplates([]);
                    }}
                    className="text-white hover:bg-purple-800 rounded-lg p-2 transition-colors"
                    aria-label="Cerrar"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                {/* Botón Seleccionar Todos */}
                <button
                  onClick={handleSelectAllTemplates}
                  className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg font-semibold text-sm transition-colors flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {allTemplatesSelected ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    )}
                  </svg>
                  {allTemplatesSelected ? "Deseleccionar todos" : "Seleccionar todos"}
                </button>
              </div>

              {/* Lista de plantillas */}
              <div className="p-6 overflow-y-auto max-h-[50vh]">
                {state.recurringTemplates
                  .filter(t => {
                    // Filtrar solo activas y que no estén ya en el mes
                    if (!t.active) return false;
                    const existingTemplateIds = allExpenses
                      .filter(e => e.isFromRecurring && e.recurringTemplateId)
                      .map(e => e.recurringTemplateId);
                    return !existingTemplateIds.includes(t.id);
                  })
                  .map(template => {
                    const isSelected = selectedTemplates.includes(template.id);
                    
                    return (
                      <div
                        key={template.id}
                        onClick={() => handleToggleTemplate(template.id)}
                        className={`mb-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                          isSelected
                            ? "border-purple-500 bg-purple-50 dark:bg-purple-900/30"
                            : "border-gray-200 dark:border-gray-600 hover:border-purple-300 dark:hover:border-purple-500 bg-white dark:bg-gray-700"
                        }`}
                      >
                        <div className="flex items-center gap-4">
                          {/* Checkbox */}
                          <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-colors ${
                            isSelected
                              ? "bg-purple-600 border-purple-600"
                              : "border-gray-300"
                          }`}>
                            {isSelected && (
                              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </div>

                          {/* Info */}
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-1">
                              <h4 className="font-bold text-lg text-gray-800 dark:text-gray-100">{template.name}</h4>
                              <span className="text-xl font-bold text-purple-600">
                                {formatDisplayAmount(template.amount)}
                              </span>
                            </div>
                            <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                              <span className="bg-gray-100 dark:bg-gray-600 px-2 py-1 rounded">
                                {getCategoryLabel(template.category, customCategories)}
                              </span>
                              <span>Vence día {template.dueDay}</span>
                              {template.remindDaysBefore > 0 && (
                                <span className="text-orange-600">
                                  🔔 {template.remindDaysBefore} {template.remindDaysBefore === 1 ? "día" : "días"} antes
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>

              {/* Footer */}
              <div className="bg-gray-50 dark:bg-gray-700/50 p-6 border-t border-gray-200 dark:border-gray-600">
                <div className="flex items-center justify-between mb-4">
                  <div className="text-sm text-gray-600">
                    <span className="font-semibold">{selectedTemplates.length}</span> {selectedTemplates.length === 1 ? "plantilla seleccionada" : "plantillas seleccionadas"}
                  </div>
                  {selectedTemplates.length > 0 && (
                    <div className="text-right">
                      <div className="text-sm text-gray-600">Total a agregar:</div>
                      <div className="text-2xl font-bold text-purple-600">
                        {formatDisplayAmount(
                          selectedTemplates.reduce((sum, tId) => {
                            const t = state.recurringTemplates.find(template => template.id === tId);
                            return sum + (t?.amount || 0);
                          }, 0)
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowRecurringModal(false);
                      setSelectedTemplates([]);
                    }}
                    className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleAddRecurringExpenses}
                    disabled={selectedTemplates.length === 0}
                    className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white font-bold rounded-xl shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Agregar {selectedTemplates.length > 0 && `(${selectedTemplates.length})`}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default MonthDetail;
